import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";
import content from "@/data/content.json";
import jobs from "@/data/jobs.json";
import { AccentH2, Container, Label, Reveal, Section } from "./Primitives";
import { FlowLines } from "./FlowLines";

const { meta } = content;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-[10px] border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-3.5 text-[16px] text-[#1e2532] outline-none placeholder:text-[#9aa1ad] focus:border-[#0f9d6e] focus:bg-white";
const labelCls = "mb-2 block text-[13.5px] font-semibold text-[#3c4356]";

export const BewerbungForm = ({
  defaultRolle = "",
  stelle = "",
  h2 = "Initiativbewerbung",
  intro = "Keine passende Stelle dabei? Schick uns deine Eckdaten — wir melden uns, sobald etwas Passendes in deiner Region dazukommt. Ein Lebenslauf ist nicht nötig, ein kurzes Gespräch reicht.",
  label = "Bewerbung",
}) => {
  const [form, setForm] = useState({
    vorname: "",
    nachname: "",
    email: "",
    phone: "",
    rolle: defaultRolle,
    bundesland: "",
    message: "",
    stelle,
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vorname || !form.nachname || !form.email || !form.phone || !form.rolle || !form.consent) {
      toast.error("Bitte fülle alle Pflichtfelder aus und bestätige die Einwilligung.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bewerbung`, form);
      setDone(true);
      toast.success("Danke! Wir melden uns innerhalb von 24 Stunden bei dir.");
    } catch (err) {
      toast.error("Die Bewerbung konnte nicht gesendet werden. Ruf uns gerne direkt an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Section
      id="bewerbung"
      testId="section-bewerbung"
      className="has-lines overflow-hidden bg-[#1e2532] text-white on-dark"
    >
      <FlowLines />
      <Container>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
          <Reveal>
            <Label>{label}</Label>
            <AccentH2 className="mt-6 font-bold">{h2}</AccentH2>
            <p className="mt-6 max-w-[430px] text-[16.5px] leading-[1.76] text-white/65">{intro}</p>
            <div className="mt-8 space-y-3">
              <a href={meta.phoneHref} className="flex items-center gap-3.5 text-[16px] text-white/85">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/14 bg-white/[0.05]">
                  <Phone size={16} className="text-[#3ecf9b]" />
                </span>
                <span className="link-underline">{meta.phone}</span>
              </a>
              <a href={`mailto:${meta.email}`} className="flex items-center gap-3.5 text-[16px] text-white/85">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/14 bg-white/[0.05]">
                  <Mail size={16} className="text-[#3ecf9b]" />
                </span>
                <span className="link-underline">{meta.email}</span>
              </a>
            </div>
            <p className="mt-6 max-w-[430px] text-[14.5px] leading-[1.7] text-white/50">
              Du hast Lebenslauf und Zeugnisse schon fertig? Dann schick sie einfach per E-Mail an {meta.email} — oder
              fülle das Formular aus, das reicht uns für den Start völlig.
            </p>
          </Reveal>

          <Reveal>
            {done ? (
              <div
                data-testid="bewerbung-success"
                className="rounded-[20px] border border-[#0f9d6e]/40 bg-white p-10 text-[#1e2532] md:p-14"
              >
                <h3 className="font-semibold text-[#1e2532]">Deine Bewerbung ist eingegangen.</h3>
                <p className="mt-4 text-[16.5px] leading-[1.78] text-[#565f70]">
                  Wir schauen uns deine Angaben an und melden uns innerhalb von 24 Stunden. Wenn es schneller gehen
                  soll, erreichst du uns unter {meta.phone}.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="bewerbung-form"
                className="rounded-[20px] border border-[#dfe2e0] bg-white p-7 text-[#1e2532] md:p-10"
                noValidate
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className={labelCls}>Vorname *</span>
                    <input data-testid="bewerbung-vorname" className={field} value={form.vorname} onChange={(e) => set("vorname", e.target.value)} autoComplete="given-name" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Nachname *</span>
                    <input data-testid="bewerbung-nachname" className={field} value={form.nachname} onChange={(e) => set("nachname", e.target.value)} autoComplete="family-name" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>E-Mail *</span>
                    <input data-testid="bewerbung-email" type="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Telefon *</span>
                    <input data-testid="bewerbung-phone" type="tel" className={field} value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Gewünschte Rolle *</span>
                    <select data-testid="bewerbung-rolle" className={field} value={form.rolle} onChange={(e) => set("rolle", e.target.value)}>
                      <option value="">Bitte wählen</option>
                      {jobs.rollen.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className={labelCls}>Bundesland</span>
                    <select data-testid="bewerbung-bundesland" className={field} value={form.bundesland} onChange={(e) => set("bundesland", e.target.value)}>
                      <option value="">Bitte wählen</option>
                      {jobs.bundeslaender.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="mt-6 block">
                  <span className={labelCls}>Nachricht</span>
                  <textarea
                    data-testid="bewerbung-message"
                    rows={4}
                    className={field}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder="Erfahrung, Verfügbarkeit, Gehaltsvorstellung"
                  />
                </label>

                <label className="mt-8 flex items-start gap-3 text-[14.5px] leading-[1.6] text-[#565f70]">
                  <input
                    data-testid="bewerbung-consent"
                    type="checkbox"
                    className="mt-1 h-[18px] w-[18px] shrink-0"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                  />
                  <span>
                    Ich habe die{" "}
                    <a href="/datenschutzerklaerung/" className="link-underline font-semibold text-[#1e2532]">
                      Datenschutzerklärung
                    </a>{" "}
                    gelesen und willige in die Verarbeitung meiner Angaben zur Bearbeitung meiner Bewerbung ein. *
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="bewerbung-submit"
                  disabled={sending}
                  className="btn-solid mt-9 inline-flex w-full items-center justify-center px-8 py-[17px] text-[14px] font-bold uppercase tracking-[0.06em] disabled:opacity-60"
                >
                  <span>{sending ? "Wird gesendet …" : "Bewerbung absenden"}</span>
                </button>
                <p className="mt-4 text-center text-[13px] text-[#8a90a0]">
                  Antwort innerhalb von 24 Stunden · Vertraulich, dein Arbeitgeber erfährt nichts
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};
